import React from "react";
import { Routes, Route, Navigate, Link } from "react-router-dom";
import { AppShell } from "./components/layout/AppShell";
import { LoginPage } from "./features/auth/LoginPage";
import { SignupPage } from "./features/auth/SignupPage";
import { DashboardHome } from "./pages/dashboard/DashboardHome";
import { ReportBuilderPage } from "./pages/dashboard/ReportBuilderPage";
import { AdminPage } from "./pages/dashboard/AdminPage";
import { SchedulesPage } from "./pages/dashboard/SchedulesPage";
import { CalendarPage } from "./pages/dashboard/CalendarPage";
import { AskPage } from "./pages/dashboard/AskPage";
import { DevicesPage } from "./pages/dashboard/DevicesPage";
import { LiveActivityPage } from "./pages/dashboard/LiveActivityPage";
import { ScanMapPage } from "./pages/dashboard/ScanMapPage";
import { CheckpointDirectoryPage } from "./pages/dashboard/CheckpointDirectoryPage";
import { UserManagementPage } from "./pages/dashboard/UserManagementPage";
import { CompliancePage } from "./pages/dashboard/CompliancePage";
import { OfficerManagementPage } from "./pages/dashboard/OfficerManagementPage";
import { OfficerDetailPage } from "./pages/dashboard/OfficerDetailPage";
import { SettingsPage } from "./features/guard/SettingsPage";
import { HistoryPage } from "./features/guard/HistoryPage";
import { ScanPage } from "./features/guard/ScanPage";
import { useAuthStore } from "./state/auth";
import { LoginForm } from "./features/auth/LoginForm";
import { DeploymentPlanner } from "./components/deploy/DeploymentPlanner";
import { CaptureWalkthrough } from "./components/deploy/CaptureWalkthrough";
import { VerifyWalkthrough } from "./components/deploy/VerifyWalkthrough";
import { useTranslation } from "./i18n";
import { ComplianceReportsSettings } from "./components/reports/ComplianceReportsSettings";

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("ErrorBoundary caught:", error, info);
  }
  render() {
    if (this.state.hasError) {
      return <ErrorFallback onReset={() => { this.setState({ hasError: false }); window.location.href = "/"; }} />;
    }
    return this.props.children;
  }
}

function ErrorFallback({ onReset }: { onReset: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-4 bg-gray-50">
      <p className="text-lg font-semibold text-gray-800">{t("landing.somethingWrong", "Something went wrong.")}</p>
      <button
        onClick={onReset}
        className="rounded-md bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700"
      >
        {t("landing.returnHome", "Return to Home")}
      </button>
    </div>
  );
}

function ProtectedRoute({ children, allowedRoles }: { children: React.ReactNode; allowedRoles?: string[] }) {
  const { user, loading, initialized } = useAuthStore();
  const { t } = useTranslation();

  if (loading || !initialized) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent"></div>
          <p className="text-sm text-gray-500">{t("common.loading", "Loading…")}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to={user.role === 'GUARD' ? '/guard' : '/supervisor'} replace />;
  }

  return <>{children}</>;
}

/**
 * Section divider used when two consolidated pages are stacked on one route
 * (e.g. Schedules under Calendar). Keeps the seam legible in both themes.
 */
function SectionDivider({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3 pt-2">
      <div className="h-px flex-1 bg-gray-200 dark:bg-[#33334d]" />
      <span className="text-xs font-bold uppercase tracking-wider text-gray-400">{label}</span>
      <div className="h-px flex-1 bg-gray-200 dark:bg-[#33334d]" />
    </div>
  );
}

// ── Consolidated pages ──────────────────────────────────────────────────────
// To keep the sidebar short, a few former standalone tabs now live beneath a
// related page. The pages render unchanged; they are simply stacked here.

function CalendarWithSchedules() {
  return (
    <div className="space-y-8">
      <CalendarPage />
      <SectionDivider label="Schedules" />
      <SchedulesPage />
    </div>
  );
}

function SettingsWithAdmin() {
  const { user } = useAuthStore();
  const role = (user?.role || "").toUpperCase();
  const isAdmin = role === "ADMIN";
  const isAdminOrDirector = isAdmin || role === "DIRECTOR" || role === "ADMINISTRATOR";
  return (
    <div className="space-y-8">
      <SettingsPage />
      {isAdminOrDirector && (
        <>
          <SectionDivider label="Compliance Reports" />
          <ComplianceReportsSettings />
          <SectionDivider label="System Admin" />
          <AdminPage />
        </>
      )}
      {isAdmin && (
        <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-[#33334d] dark:bg-[#24243e]">
          <h2 className="mb-1 text-lg font-semibold text-gray-900 dark:text-white">Deployment Tools</h2>
          <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
            Site rollout walkthroughs — plan checkpoint placement, capture reference imagery, and verify coverage.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/supervisor/deploy/plan"
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 dark:border-[#33334d] dark:bg-[#1f1f38] dark:text-gray-200 dark:hover:bg-[#2a2a47]"
            >
              Plan Deployment
            </Link>
            <Link
              to="/supervisor/deploy/capture"
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 dark:border-[#33334d] dark:bg-[#1f1f38] dark:text-gray-200 dark:hover:bg-[#2a2a47]"
            >
              Capture Walkthrough
            </Link>
            <Link
              to="/supervisor/deploy/verify"
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 dark:border-[#33334d] dark:bg-[#1f1f38] dark:text-gray-200 dark:hover:bg-[#2a2a47]"
            >
              Verify Walkthrough
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />

      <Route
        path="/supervisor"
        element={
          <ProtectedRoute allowedRoles={['SUPERVISOR', 'DIRECTOR', 'ADMIN']}>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardHome />} />
        <Route path="reports" element={<Navigate to="/supervisor/report-builder" replace />} />
        <Route path="report-builder" element={<ReportBuilderPage />} />
        <Route path="schedules" element={<SchedulesPage />} />
        <Route path="calendar" element={<CalendarWithSchedules />} />
        <Route
          path="admin"
          element={
            <ProtectedRoute allowedRoles={['ADMIN', 'DIRECTOR']}>
              <AdminPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="ask"
          element={
            <ProtectedRoute allowedRoles={['ADMIN', 'DIRECTOR']}>
              <AskPage />
            </ProtectedRoute>
          }
        />
        {/* The standalone Alerts tab is retired — its content now lives at the
            bottom of the Compliance page. Redirect old links and bookmarks. */}
        <Route path="alerts" element={<Navigate to="/supervisor/compliance" replace />} />
        {/* Compliance is a monitoring view — open to the same tiers as alerts,
            including the view-only SUPERVISOR. */}
        <Route path="compliance" element={<CompliancePage />} />
        <Route path="devices" element={<DevicesPage />} />
        <Route path="activity" element={<LiveActivityPage />} />
        <Route path="checkpoints" element={<CheckpointDirectoryPage />} />
        {/* Officer roster. Supervisors manage the officers at their own site, so
            this is not narrowed to ADMIN/DIRECTOR — the API scopes every read and
            write to the caller's hospital grants. */}
        <Route path="officers" element={<OfficerManagementPage />} />
        <Route path="officers/:id" element={<OfficerDetailPage />} />
        {/* Patrol Reports now lives at the top of the Officers page; redirect
            old links and bookmarks. */}
        <Route path="patrol-reports" element={<Navigate to="/supervisor/officers" replace />} />
        <Route path="scan-map" element={<ScanMapPage />} />
        <Route path="users" element={<UserManagementPage />} />
        <Route
          path="settings"
          element={
            <ProtectedRoute allowedRoles={['ADMIN', 'DIRECTOR', 'SUPERVISOR']}>
              <SettingsWithAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="deploy/plan"
          element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <DeploymentPlanner />
            </ProtectedRoute>
          }
        />
        <Route
          path="deploy/capture"
          element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <CaptureWalkthrough />
            </ProtectedRoute>
          }
        />
        <Route
          path="deploy/verify"
          element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <VerifyWalkthrough />
            </ProtectedRoute>
          }
        />
      </Route>

      <Route
        path="/guard"
        element={
          <ProtectedRoute allowedRoles={['GUARD']}>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route index element={<HistoryPage />} />
        <Route path="scan" element={<ScanPage />} />
      </Route>

      {/* Catch-all: redirect unknown routes to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </ErrorBoundary>
  );
}

function LandingPage() {
  const { user, loading, initialized } = useAuthStore();
  const { t } = useTranslation();

  // While auth is resolving, show a minimal loading state
  if (loading || !initialized) {
    return (
      <div className="min-h-screen bg-white">
        <header className="brand-header-blue-gradient border-b border-white/10 shadow-sm relative overflow-hidden">
          <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4">
            <div className="flex items-center gap-3 text-xl font-bold text-white">
              <img src="/g-data-logo-no-bg.png" alt="G-Data" className="h-10 w-10 object-contain" />
              <span>GuardAI by G-Data Labs</span>
            </div>
          </div>
        </header>
        <div className="flex items-center justify-center py-32">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent"></div>
        </div>
      </div>
    );
  }

  // If user is already logged in, redirect to their dashboard
  if (user) {
    return <Navigate to={user.role === 'GUARD' ? '/guard' : '/supervisor'} replace />;
  }

  return (
    <div className="min-h-screen bg-white">
      <header className="brand-header-blue-gradient border-b border-white/10 shadow-sm relative overflow-hidden">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4">
          <div className="flex items-center gap-3 text-xl font-bold text-white">
            <img src="/g-data-logo-no-bg.png" alt="G-Data" className="h-10 w-10 object-contain" />
            <span>GuardAI by G-Data Labs</span>
          </div>
          <Link to="/login" className="text-sm font-medium text-white hover:underline">
            {t("auth.signIn", "Sign in")}
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-16 sm:py-24">
        <section className="grid gap-10 lg:grid-cols-2 items-start">
          <div className="space-y-6">
            <header className="space-y-4">
              <h1 className="text-3xl font-semibold text-gray-900">{t("brand.name", "GuardAI by G-Data Labs")}</h1>
              <p className="text-base text-gray-600">
                {t("auth.tagline", "Security guards scan their badge at any station and supervisors see updates instantly on a secure dashboard.")}
              </p>
              <ul className="space-y-2 text-sm text-gray-700">
                <li>• {t("landing.bulletMonitoring", "Real-time monitoring for every lobby, entrance, and corridor.")}</li>
                <li>• {t("landing.bulletIntegrity", "Integrity analytics for missed scans and flagged events.")}</li>
                <li>• {t("landing.bulletWorkspace", "Unified supervisor workspace with instant search and AI insights.")}</li>
              </ul>
            </header>
          </div>
          <div className="w-full">
            <LoginForm
              showSignupLink={false}
              heading={t("landing.signInToGuard", "Sign in to Guard")}
              subheading={t("landing.signInSubheading", "Use your supervisor or guard credentials to continue.")}
            />
          </div>
        </section>
      </main>
    </div>
  );
}

function NavButton({ to, children, primary }: { to: string; children: React.ReactNode; primary?: boolean }) {
  const className = primary
    ? "rounded-md px-4 py-2 text-white transition-colors brand-blue-gradient text-sm"
    : "rounded-md border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors text-sm";
  return (
    <Link className={className} to={to}>
      {children}
    </Link>
  );
}

export default App;
